<template>
  <div class="customer-order-details-component mx-auto" v-if="order">
    <div class="mb-5">
      <a class="cursor-pointer flex items-center" @click="back">
        <img loading="lazy" src="/img/icons/left-arrow.svg" class="mr-2" />
        <span class="go-back-link font-bold">{{
          trans.get("customer.go-back")
        }}</span>
      </a>
    </div>
    <h2 class="mb-10">{{ trans.get("customer.order-details") }}</h2>
    <div class="details">
      <div class="flex space-x-16">
        <div class="w-1/2">
          <p class="font-bold mb-2">{{ trans.get("customer.order-status") }}</p>
          <span class="chip" :class="order.status">{{
            trans.get("customer." + order.status)
          }}</span>
        </div>
        <div class="w-1/2">
          <p class="font-bold mb-1">
            {{ trans.get("customer.expected-delivery-date") }}
          </p>
          <p>{{ order.delivery_date }}</p>
        </div>
      </div>
      <hr class="w-full my-10" />
      <div class="flex space-x-16">
        <div class="w-1/2">
          <p class="font-bold mb-2">{{ trans.get("customer.order-code") }}</p>
          <p>{{ order.code }}</p>
        </div>
        <div class="w-1/2">
          <p class="font-bold mb-1">{{ trans.get("customer.order-date") }}</p>
          <p>{{ order.created_at }}</p>
        </div>
      </div>
      <hr class="w-full my-10" />
      <div class="flex space-x-16">
        <div class="w-1/2">
          <p class="font-bold mb-2">
            {{ trans.get("customer.shipping-address") }}
          </p>
          <p>{{ order.shipping_address }}, {{order.info}}</p>
          <p>{{order.city}}, {{order.postal}}. {{order.country}}</p>
        </div>
        <div class="w-1/2">
          <p class="font-bold mb-1">{{ trans.get("customer.country") }}</p>
          <p>{{ order.country }}</p>
        </div>
      </div>
      <hr class="w-full my-10" />
        <div class="flex space-x-16">
          <div class="w-1/2">
            <p class="font-bold mb-2">{{ trans.get("customer.shipping-type") }}</p>
            <p>{{ trans.get("customer." + order["shipping_type"]) }}</p>
          </div>
          <div class="w-1/2">
            <p class="font-bold mb-1">{{ trans.get("customer.payment-data") }}</p>
            <p>{{ paymentDesc }}</p>
          </div>
        </div>
      <hr class="w-full my-10" />
      <div>
        <p class="font-bold mb-5">
          {{ productsCount }}
          {{ trans.choice("customer.article_s", productsCount) }}
        </p>
        <div class="grid-products">
          <div
            class="product p-5 rounded"
            v-for="product in order.products"
            :key="product.id"
          >
            <p class="font-bold mb-3">{{ product.pivot.product_name[lang] }}</p>
            <div class="flex space-x-8 items-center">
              <img
                class="image"
                v-if="product.images.length"
                :src="product.images[0].webp"
                :alt="product.pivot.product_name"
              />
              <img loading="lazy" class="image" v-else src="/img/no-image-placeholder.png" />
              <div>
                <p class="flex flex-wrap">
                  <span class="font-bold"
                    >{{ trans.get("customer.price") }}:&nbsp;&nbsp;</span
                  >
                  <span>{{ product.pivot.price | currency }}</span>
                </p>
                <p class="flex flex-wrap">
                  <span class="font-bold"
                    >{{ trans.get("customer.quantity") }}:&nbsp;&nbsp;</span
                  >
                  <span>{{ product.pivot.quantity }}</span>
                </p>
              </div>
            </div>
            <div class="mt-4">
                <promotion-card :image="product.pivot.promotion_image" :text="product.pivot.promotion_text"></promotion-card>
            </div>
          </div>
        </div>
      </div>
      <hr class="w-full my-10" />
      <div>
        <p class="font-bold mb-5">{{ trans.get("customer.order-total") }}</p>
        <div class="flex justify-between">
          <span>{{ trans.get("customer.subtotal") }}</span>
          <span>{{ order.subtotal | currency }}</span>
        </div>
        <div v-if="order.coupon_name" class="flex justify-between mb-2">
            <span>{{ trans.get('wizard.coupon') }}</span>
            <span v-if="order.coupon_type === 'freesend'">{{trans.get('wizard.shipping')}} {{ trans.get('wizard.free') }}</span>
            <span v-else>{{order.discount | currency }}</span>
        </div>
        <div class="flex justify-between">
          <span>{{ trans.get("customer.shipping-price") }}</span>
          <span>{{ order.shipping_price | currency }}</span>
        </div>
      </div>
      <hr class="w-full my-10" />
      <div class="flex justify-between">
        <span class="font-bold">{{ trans.get("customer.total") }}</span>
        <span>{{ order.total | currency }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import mixin, { goToRouteWithName } from "../../mixin";
import store from "../../store";
import PromotionCard from "../../components/cards/PromotionCard.vue";

export default {
  mixins: [mixin, goToRouteWithName],
  components: {
    PromotionCard
  },
  data() {
    return {
      order: null,
    };
  },

  computed: {
    productsCount() {
      return this.order.products.length;
    },
    paymentDesc() {
      let text = "";

      switch (this.order.payment_methdo) {
        case "payland":
          text = this.trans.get("customer.credit-card");
          break;
        case "bitcoin":
          text = this.trans.get("customer.bitcoin-payment");
          break;
        default:
          text = this.trans.get("customer.credit-card");
          break;
      }

      return text;
    },
  },

  filters: {
    currency: function (amount) {
      return (
        new Intl.NumberFormat("es-ES", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount) + " €"
      );
    },
  },

  methods: {
    back() {
        this.$router.push({ name: "customer.my-orders" });
    },
  },

  beforeRouteEnter(to, from, next) {
    const order = Object.values(store.state.customer.storedData.orders)
      .flat()
      .find((order) => order.code === to.params.code);
    if (order) {
      next((vm) => (vm.order = order));
    } else {
      next({ replace: true, name: "customer.my-orders" });
    }
  },
};
</script>

<style lang="scss" scoped>
.customer-order-details-component {
  max-width: 800px;

  hr {
    border-top: 1px solid #ccc;
  }

  .grid-products {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;

    @media (min-width: 640px) {
      grid-template-columns: 1fr 1fr;
    }
    @media (min-width: 1024px) {
      gap: 2rem 3rem;
    }

    .product {
      border: 1px solid #ccc;
    }

    .image {
      width: 6rem;
      height: 6rem;
      object-fit: contain;
      border: 1px solid #ccc;

      @media (min-width: 768px) {
        width: 8rem;
        height: 8rem;
      }
      @media (min-width: 1366px) {
        width: 10rem;
        height: 10rem;
      }
    }
  }
}
</style>
